import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

export default function SCSSCalculatorScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [principal, setPrincipal] = useState('500000');
  const [rate, setRate] = useState('8.2');
  const [years, setYears] = useState('5');

  const num = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return d;
    return n;
  };

  const data = useMemo(() => {
    const P = Math.max(0, num(principal, 0));
    const r = Math.max(0, num(rate, 0)) / 100;
    const y = Math.max(0, num(years, 0));
    const quarterlyInterest = P * r / 4; // Interest credited quarterly (not compounded)
    const totalQuarters = y * 4;
    const totalInterest = quarterlyInterest * totalQuarters;
    const maturity = P; // principal returned
    return { quarterlyInterest, totalQuarters, totalInterest, maturity };
  }, [principal, rate, years]);

  const resetAll = () => {
    setPrincipal('500000');
    setRate('8.2');
    setYears('5');
  };

  const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>SCSS Calculator</Text>
          <Text style={styles.heroSubtitle}>Senior Citizen Savings Scheme payouts</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Inputs</Text>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Investment Amount (₹)</Text>
                <TextInput value={principal} onChangeText={setPrincipal} style={styles.input} keyboardType="numeric" placeholder="e.g. 500000" />
              </View>
              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Interest Rate (%)</Text>
                  <TextInput value={rate} onChangeText={setRate} style={styles.input} keyboardType="numeric" placeholder="e.g. 8.2" />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Tenure (Years)</Text>
                  <TextInput value={years} onChangeText={setYears} style={styles.input} keyboardType="numeric" placeholder="e.g. 5" />
                </View>
              </View>
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Quarterly Payout</Text>
                <Text style={styles.panelSubtitle}>Interest credited each quarter</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Quarterly Interest</Text><Text style={styles.summaryValue}>{currency.format(data.quarterlyInterest)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Quarters</Text><Text style={styles.summaryValue}>{formatNumber(data.totalQuarters)}</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Maturity Summary</Text>
                <Text style={styles.panelSubtitle}>Total interest earned</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Interest</Text><Text style={styles.summaryValue}>{currency.format(data.totalInterest)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Maturity Value</Text><Text style={styles.summaryValue}>{currency.format(data.maturity)}</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderSEO(navigation)}
      {renderFAQ()}
      <Footer />
    </ScrollView>
  );
}

function renderSEO(navigation) {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>About SCSS</Text>
      <Text style={styles.seoText}>
        The Senior Citizen Savings Scheme (SCSS) is a government-backed savings product designed for senior citizens. It typically offers an attractive fixed interest rate credited quarterly, with principal repaid at maturity. Use this calculator to estimate quarterly payouts, total interest, and maturity value.
      </Text>

      <Text style={styles.seoSubtitle}>How interest is paid</Text>
      <Text style={styles.seoText}>
        Interest accrues on the deposit and is credited every quarter. This tool assumes simple quarterly payouts (no compounding) and shows the number of quarters, quarterly interest, and cumulative interest.
      </Text>

      <Text style={styles.seoSubtitle}>Eligibility and tax</Text>
      <Text style={styles.seoText}>
        SCSS is typically available to individuals aged 60 years or above, subject to scheme rules. Deposits may be eligible for tax deduction under Section 80C, while interest income is generally taxable as per prevailing laws. Always refer to official notifications for the latest rules.
      </Text>

      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('PPFCalculator')}>
          <Text style={styles.linkText}>PPF Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('NSCCalculator')}>
          <Text style={styles.linkText}>NSC Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('APYCalculator')}>
          <Text style={styles.linkText}>APY Calculator</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.disclaimer}>
        This calculator provides indicative results only. Terms, interest rates, limits, and tax rules are subject to change by regulation. Please verify details with official sources before investing.
      </Text>
    </View>
  );
}

function renderFAQ() {
  return (
    <View style={styles.faqContainer}>
      <Text style={styles.faqTitle}>Frequently Asked Questions</Text>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Is SCSS interest compounded?</Text>
        <Text style={styles.faqAnswer}>Interest is credited every quarter. This tool models quarterly payouts without compounding.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Do SCSS deposits qualify for Section 80C?</Text>
        <Text style={styles.faqAnswer}>Deposits may be eligible for Section 80C deduction subject to prevailing tax provisions. Always confirm current rules.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Is the interest income taxable?</Text>
        <Text style={styles.faqAnswer}>Yes, interest is typically taxable as per your applicable tax slab and rules in force.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Can the account be extended after maturity?</Text>
        <Text style={styles.faqAnswer}>Extensions may be permitted as per scheme guidelines. Refer to official notifications for current terms.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Is premature withdrawal allowed?</Text>
        <Text style={styles.faqAnswer}>Premature closure is generally allowed with a penalty as per rules. Check current policy before acting.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 24 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 18, paddingHorizontal: 16 },
  heroContent: { maxWidth: 1200, alignSelf: 'center' },
  heroTitle: { fontSize: 22, fontWeight: '800', color: '#ffffff', marginBottom: 4 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginVertical: 6 },
  input: { backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  label: { fontSize: 13, color: '#334155', marginBottom: 6 },
  flexItem: { flex: 1 },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: '#334155' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  buttonPrimary: { backgroundColor: '#4F46E5', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { color: '#ffffff', fontWeight: '600' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  seoSubtitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 6 },
  seoText: { color: '#334155', lineHeight: 20 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  linkButton: { backgroundColor: '#EEF2FF', borderColor: '#c7d2fe', borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  linkText: { color: '#1e293b', fontWeight: '600' },
  disclaimer: { color: '#64748b', fontSize: 12, marginTop: 10 },
  faqContainer: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  faqTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  faqItem: { marginBottom: 10 },
  faqQuestion: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
  faqAnswer: { color: '#334155', marginTop: 4, lineHeight: 20 },
});